import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Box, Card, CardContent } from "@mui/material";
import { getQueryStringParams } from "@theatticusapp/atticus-shared-components";
import { FormItem } from "../shared/FormItem";
import { State } from "../../models/Geography";
import { useAuthContext } from "../../AuthContextWrapper";
import { ScreenContentWrapper } from "../shared/ScreenContentWrapper";
import { formsApi } from "../../apis";
import { FeedbackCheck } from "../shared/FeedbackCheck";
import { SearchAndFilter, SearchQuery } from "../shared/SearchAndFilter";
import { NoSearchResults } from "../shared/NoSearchResults";
import { NoStateForms } from "../shared/NoStateForms";
import { AtticusForm } from "../../models/AtticusForm";
import { AtticusFormViewDialog } from "../shared/AtticusFormViewDialog";
import { Loading } from "views/shared/Loading";

export const FormsLibraryScreen = (): JSX.Element => {
  const { activeEstate, states } = useAuthContext()
  const navigate = useNavigate();
  const [forms, setForms] = useState<AtticusForm[]>();
  const [loading, setLoading] = useState(true);
  const { search_terms, language_id } = getQueryStringParams();
  const [languageId, setLanguageId] = useState<number>(language_id || 1);
  const [searchTerms, setSearchTerms] = useState<string>(search_terms || "");
  const [dialogForm, setDialogForm] = useState<AtticusForm>();

  const state = states?.find(
    (state: State) => state?.id === activeEstate?.stateId
  );

  useEffect(() => {
    setLanguageId(language_id);
    setSearchTerms(search_terms);
  }, [language_id, search_terms]);

  useEffect(() => {
    // refresh the active estate
    (async () => {
      setLoading(true);
      await getForms(languageId, searchTerms);
      setLoading(false);
    })();
  }, [activeEstate, searchTerms, languageId]);

  const getForms = async (
    languageId = 1,
    searchTerms = ""
  ): Promise<boolean> => {
    if (activeEstate?.id && state?.id) {
      setForms(
        await formsApi.getStateProbateForms(activeEstate.id, state.id, searchTerms, languageId)
      );
      return true;
    } else {
      return false;
    }
  };

  const handleSearch = async (searchQuery: SearchQuery): Promise<boolean> => {
    const pathName = window.location.pathname;
    // when defaults, reset
    if (searchQuery.searchTerms === "" && searchQuery.languageId === 1) {
      navigate(pathName);
    } else {
      navigate(
        `${location.pathname}?search_terms=${encodeURI(
          searchQuery.searchTerms || ""
        )}&language_id=${searchQuery.languageId || ""}`
      );
    }
    return true;
  };

  const isSearchDefault = (): boolean => {
    return languageId === undefined && searchTerms === undefined;
  };

  const renderForms = (): JSX.Element[] | JSX.Element => {
    return forms?.length ? (
      forms?.map((form: AtticusForm) => {
        return (
          <Card
            sx={{
              my: 2,
            }}
            key={`${form.formTitle}_${Math.random()}`}
          >
            <CardContent>
              <FormItem
                form={form}
                state={state}
                displayHelp={true}
                onShowForm={() => setDialogForm(form)}
                verticalPaddingUnit={0}
              />
            </CardContent>
          </Card>
        );
      })
    ) : (
      <></>
    );
  };

  const pageName = state?.name ? `${state?.name} Forms` : "Forms";

  return (
    <ScreenContentWrapper
      pageSuffix={pageName}
      title={pageName}
      hideImage={true}
    >
      <Box sx={{ my: 4 }}>
        <AtticusFormViewDialog
          open={!!dialogForm}
          onClose={() => {
            setDialogForm(undefined);
          }}
          form={dialogForm}
          estate={activeEstate}
          showAddToFileCabinet={true}
        />

        {/* USE CASES: */}
        {/*   PROBATE UNDETERMINED PAID/UNPAID - show determination */}
        {/*   PROBATE DETERMINED UNPAID - show determination & upgrade */}
        {/*   PROBATE DETERMINED PAID - show determination & forms */}
        {/*<ProbateDeterminedCard
          probate={activeEstate?.probate}
          state={state?.name}
          onClick={() => navigate(SCREEN_PATHS.probatePacket)}
        />*/}

        {state && (
          <SearchAndFilter
            onSubmit={handleSearch}
            searchTerms={searchTerms}
            languageId={languageId}
          />
        )}
        {!loading ? (
          <Box sx={{ my: 4 }}>
            {forms?.length ? (
              <Box>
                {renderForms()}
                <FeedbackCheck screenName="FormsLibrary-Results" />
              </Box>
            ) : isSearchDefault() ? (
              <NoStateForms state={state} />
            ) : (
              <NoSearchResults
                onReset={() => handleSearch({ searchTerms: "", languageId: 1 })}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              my: 4,
            }}
          >
            <Loading />
          </Box>
        )}
      </Box>
    </ScreenContentWrapper>
  );
};
